// Bounded, detached host-internal G07 query data. Never accept rendering objects or callbacks.
export const PLAN_QUERY_LIMITS = Object.freeze({ records: 100000, layers: 1024, owners: 64,
  idLength: 256, characters: 4000000, coordinate: 1e9 });

function check(condition, message) { if (!condition) throw new TypeError(`Plan query: ${message}`); }
function record(value, required, optional = []) {
  check(value && typeof value === 'object' && !Array.isArray(value), 'expected data record');
  const proto = Object.getPrototypeOf(value);
  check(proto === Object.prototype || proto === null, 'custom prototype');
  const result = {};
  for (const key of Reflect.ownKeys(value)) {
    check(typeof key === 'string' && (required.includes(key) || optional.includes(key)), 'unknown field');
    const property = Object.getOwnPropertyDescriptor(value, key);
    check(property?.enumerable && Object.hasOwn(property, 'value'), 'accessors are forbidden');
    result[key] = property.value;
  }
  for (const key of required) check(Object.hasOwn(result, key), `missing ${key}`);
  return result;
}
function list(value, limit) {
  check(Array.isArray(value) && Object.getPrototypeOf(value) === Array.prototype, 'expected data array');
  const length = Object.getOwnPropertyDescriptor(value, 'length')?.value;
  check(Number.isSafeInteger(length) && length <= limit && Reflect.ownKeys(value).length === length + 1, 'array budget or shape');
  const result = [];
  for (let i = 0; i < length; i++) {
    const property = Object.getOwnPropertyDescriptor(value, String(i));
    check(property?.enumerable && Object.hasOwn(property, 'value'), 'sparse/accessor array');
    result.push(property.value);
  }
  return result;
}
function number(value, min = -PLAN_QUERY_LIMITS.coordinate, max = PLAN_QUERY_LIMITS.coordinate) {
  check(Number.isFinite(value) && value >= min && value <= max, 'invalid number');
  return value;
}
function boolean(value) { check(typeof value === 'boolean', 'expected boolean'); return value; }
function point(value) {
  const p = record(value, ['x', 'z']);
  return Object.freeze({ x: number(p.x), z: number(p.z) });
}
function bounds(value) {
  const b = record(value, ['minX', 'minZ', 'maxX', 'maxZ']);
  Object.values(b).forEach(v => number(v));
  check(b.minX <= b.maxX && b.minZ <= b.maxZ, 'inverted bounds');
  return Object.freeze(b);
}
function geometry(value) {
  // Read the discriminant without invoking an untrusted getter.
  const kind = Object.getOwnPropertyDescriptor(value || {}, 'kind')?.value;
  if (kind === 'point') {
    const g = record(value, ['kind', 'point']);
    return Object.freeze({ kind, point: point(g.point) });
  }
  if (kind === 'segment') {
    const g = record(value, ['kind', 'start', 'end']);
    return Object.freeze({ kind, start: point(g.start), end: point(g.end) });
  }
  check(kind === 'box', 'unsupported hit geometry');
  const g = record(value, ['kind', 'center', 'axis', 'halfX', 'halfZ']);
  const axis = point(g.axis), length = Math.hypot(axis.x, axis.z);
  check(Math.abs(length - 1) <= 1e-9, 'box axis must be a unit vector');
  return Object.freeze({ kind, center: point(g.center),
    axis: Object.freeze({ x: axis.x / length, z: axis.z / length }),
    halfX: number(g.halfX, 0), halfZ: number(g.halfZ, 0) });
}
function tolerance(value) {
  const t = record(value, ['cssPx', 'minMm'], ['maxMm']);
  number(t.cssPx, 0, 4096); number(t.minMm, 0);
  if (Object.hasOwn(t, 'maxMm')) number(t.maxMm, t.minMm);
  return Object.freeze(t);
}

/** Owner is intentionally absent; only the host service attaches canonical ownership. */
export function normalizePlanQueryData(source) {
  const packet = record(source, ['layers', 'hits', 'boxes', 'snaps']);
  let characters = 0, count = 0;
  const id = value => {
    check(typeof value === 'string' && value.length > 0 && value.length <= PLAN_QUERY_LIMITS.idLength
      && value.trim() === value, 'invalid identifier');
    characters += value.length;
    check(characters <= PLAN_QUERY_LIMITS.characters, 'character budget');
    return value;
  };
  const unique = (values, normalize, limit) => {
    const ids = new Set();
    return Object.freeze(list(values, limit).map(value => {
      const item = normalize(value);
      check(!ids.has(item.id), 'duplicate identifier'); ids.add(item.id);
      return Object.freeze(item);
    }));
  };
  const layers = unique(packet.layers, value => {
    const l = record(value, ['id', 'visible', 'locked']);
    return { id: id(l.id), visible: boolean(l.visible), locked: boolean(l.locked) };
  }, PLAN_QUERY_LIMITS.layers);
  const layerIds = new Set(layers.map(l => l.id));
  const base = ['id', 'objectId', 'kind', 'layerId', 'visible', 'locked'];
  const targets = (collection, extra, optional, normalize) => unique(packet[collection], value => {
    check(++count <= PLAN_QUERY_LIMITS.records, 'record budget');
    const item = record(value, [...base, ...extra], optional);
    for (const key of ['id', 'objectId', 'kind', 'layerId']) item[key] = id(item[key]);
    check(layerIds.has(item.layerId), 'unknown layer');
    boolean(item.visible); boolean(item.locked);
    if (Object.hasOwn(item, 'featureId')) item.featureId = id(item.featureId);
    return normalize(item);
  }, PLAN_QUERY_LIMITS.records);
  const hits = targets('hits', ['priority', 'order', 'geometry', 'tolerance'], ['featureId'], h => ({ ...h,
    priority: number(h.priority), order: number(h.order), geometry: geometry(h.geometry), tolerance: tolerance(h.tolerance) }));
  const boxes = targets('boxes', ['bounds'], [], b => ({ ...b, bounds: bounds(b.bounds) }));
  // A window query requires the bounds of the whole selectable object. Separate
  // fragment boxes would incorrectly select it when only one fragment is enclosed.
  const boxObjects = new Set();
  for (const box of boxes) {
    const key = JSON.stringify([box.kind, box.objectId]);
    check(!boxObjects.has(key), 'box requires one complete bound per selectable object');
    boxObjects.add(key);
  }
  const snaps = targets('snaps', ['featureId', 'point', 'priority'], [], s => ({ ...s,
    point: point(s.point), priority: number(s.priority) }));
  return Object.freeze({ layers, hits, boxes, snaps, recordCount: count, characters });
}
